import { Component, inject, signal, TemplateRef } from '@angular/core';
import { ButtonComponent } from '@progress/kendo-angular-buttons';
import { Event, NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router } from '@angular/router';
import { AsyncPipe, NgClass, NgTemplateOutlet } from '@angular/common';
import { filter } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { AuthService } from '@auth0/auth0-angular';
import { BackButtonService } from '../../services/BackbuttonService';
import { AppBarContentService } from '@stock-core/services/AppBarContent.service';
import { BackButton } from '@stock-core/models/BackButton';
import { environment } from '@environments';
import { TranslateModule } from '@ngx-translate/core';
import { ApplicationUpdateService, CapitalizePipe, signOut } from 'shared';

/** `updating`: a new version was found and the app is about to reload into it. */
type UpdateStatus = 'idle' | 'checking' | 'updating';

@Component({
  selector: 'rc-app-bar',
  standalone: true,
  templateUrl: './app-bar.component.html',
  styleUrl: './app-bar.component.scss',
  imports: [ButtonComponent, AsyncPipe, NgClass, NgTemplateOutlet, TranslateModule, CapitalizePipe],
})
export class AppBarComponent {
  #router = inject(Router);
  authService = inject(AuthService);
  #backButtonService = inject(BackButtonService);
  #appBarContentService = inject(AppBarContentService);
  #applicationUpdateService = inject(ApplicationUpdateService);
  backButtonValue?: BackButton;
  #navigationEnd$ = this.#router.events.pipe(
    filter((e: Event) => e instanceof NavigationEnd),
  ) as Observable<NavigationEnd>;
  #navigationStart$ = this.#router.events.pipe(
    filter((e: Event) => e instanceof NavigationStart),
  ) as Observable<NavigationStart>;
  isHome = false;
  #backButtonBeforeNavigation?: BackButton;
  contentTemplate: TemplateRef<typeof TemplateRef> | null;

  readonly updateStatus = signal<UpdateStatus>('idle');

  constructor() {
    this.#navigationEnd$.subscribe((navEnd) => {
      this.isHome = navEnd.url.split('?')[0] === '/home' || navEnd.url.split('?')[0] === '/';
    });
    this.#navigationStart$.subscribe(() => {
      this.#appBarContentService.clearAppBarContent();
      // Each page sets its own back button when it is created; don't leave the previous page's one behind.
      this.#backButtonBeforeNavigation = this.#backButtonService.backButtonSubject.value;
      this.#backButtonService.backButtonSubject.next(undefined);
    });
    // The navigation didn't happen (e.g. a page failed to load), so the current page keeps its back button.
    this.#router.events
      .pipe(filter((e: Event) => e instanceof NavigationCancel || e instanceof NavigationError))
      .subscribe(() => {
        if (!this.#backButtonService.backButtonSubject.value) {
          this.#backButtonService.backButtonSubject.next(this.#backButtonBeforeNavigation);
        }
      });

    this.#backButtonService.backButtonSubject.subscribe((backButton) => {
      this.backButtonValue = backButton;
    });

    this.#appBarContentService.appBarContent$.subscribe((appBarContent) => {
      this.contentTemplate = appBarContent;
    });
  }

  getAppBarContent() {
    return this.#appBarContentService.appBarContent$;
  }

  navigateToHome(): void {
    this.#router.navigate(['/']);
  }

  navigateBack(backButton: BackButton): void {
    if (backButton.path) {
      this.#router.navigate([backButton.path], { queryParams: backButton.params });
    }
    if (backButton.action) {
      backButton.action();
    }
  }

  async checkForUpdates(): Promise<void> {
    this.updateStatus.set('checking');
    const result = await this.#applicationUpdateService.checkNow();
    // Only an available update is worth telling the user about; the app reloads into it right after.
    this.updateStatus.set(result === 'updating' ? 'updating' : 'idle');
  }

  logout(): void {
    signOut(this.authService, environment.auth0.clientId);
  }
}
